import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Banner from "@/components/Banner";
import Checkbox from "@/components/Checkbox";
import Select from "@/components/Select";
import SideSheetModal from "@/components/SideSheetModal";
import SubSectionHeading from "@/components/SubSectionHeading";
import {
  DOMAIN,
  PERMISSION,
  useCanSendNationalCommitteeGroupEmailBool,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { copyTextToClipboard } from "@/lib/copyToClipboard";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { AuthorizationContext } from "@/providers/AuthorizationProvider";
import { GROUP_EMAIL_MAX_SELECTED_COMMITTEES, NATIONAL_SECTION_NAME, resolveNationalSectionId } from "../constants";
import { COMMITTEE_ASSIGNMENT_TITLES_QUERY, NATIONAL_SECTION_QUERY } from "../queries";
import type { CommitteeAssignmentTitle, CrmSectionOption } from "../types";
import {
  assignmentRoleFilter,
  buildCommitteeMailtoHref,
  collectEmailsFromAssignmentNodes,
  fetchGroupEmailParticipantEmails,
  formatEmailAddressList,
  groupEmailMailtoSubject,
  isMailtoTooLong,
  openMailtoHref,
  type CommitteeParticipantEmailNode,
  type MailtoField
} from "./committeeMailto";
import { fetchActiveCommitteesForGroupEmail, fetchCrmSectionsForGroupEmail } from "./groupEmailCatalog";
import {
  assignedSectionLegacyIdsForGroupEmail,
  defaultGroupEmailScopeKey,
  filterAssignedCrmSections,
  resolveGroupEmailScopes
} from "./groupEmailScopes";
import {
  GroupEmailActions,
  GroupEmailAddToField,
  GroupEmailCheckboxList,
  GroupEmailLoadingStatus,
  GroupEmailRecipientStatus
} from "./groupEmailUi";

export type GroupEmailComposerProps = {
  active: boolean;
  preferredSectionId?: string;
  layout: "sheet" | "page";
  onClose?(): void;
};

const EMPTY_RESULT = {
  emails: [] as string[],
  participantCount: 0,
  excludedCount: 0
};

export default function GroupEmailComposer({ active, preferredSectionId, layout, onClose }: GroupEmailComposerProps) {
  const client = useApolloClient();
  const { sectionLegacyIds } = useContext(AuthorizationContext);
  const canAccessNationalGroupEmail = useCanSendNationalCommitteeGroupEmailBool();
  const canViewPersonContact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonContact
  });
  const assignedLegacyIds = useMemo(() => assignedSectionLegacyIdsForGroupEmail(sectionLegacyIds), [sectionLegacyIds]);
  const needsAssignedSections = assignedLegacyIds.length > 0;

  const [crmSections, setCrmSections] = useState<CrmSectionOption[]>([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionsError, setSectionsError] = useState(false);
  const [selectedScopeKey, setSelectedScopeKey] = useState("");
  const [coreOnly, setCoreOnly] = useState(false);
  const [committees, setCommittees] = useState<Array<{ id: string; name: string }>>([]);
  const [committeesLoading, setCommitteesLoading] = useState(false);
  const [committeesError, setCommitteesError] = useState(false);
  const [committeesTruncated, setCommitteesTruncated] = useState(false);
  const [selectedCommitteeIds, setSelectedCommitteeIds] = useState<Set<string>>(new Set());
  const [selectedTitleIds, setSelectedTitleIds] = useState<Set<string>>(new Set());
  const [field, setField] = useState<MailtoField>("bcc");
  const [assignmentNodes, setAssignmentNodes] = useState<CommitteeParticipantEmailNode[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsTruncated, setRecipientsTruncated] = useState(false);
  const [copying, setCopying] = useState(false);
  const [sectionsTruncated, setSectionsTruncated] = useState(false);
  const didSeedTitlesRef = useRef(false);
  const assignmentCacheRef = useRef(new Map<string, { nodes: CommitteeParticipantEmailNode[]; truncated: boolean }>());

  const {
    data: titlesData,
    loading: titlesLoading,
    error: titlesError
  } = useQuery(COMMITTEE_ASSIGNMENT_TITLES_QUERY, {
    skip: !active
  });
  const assignmentTitles: CommitteeAssignmentTitle[] = titlesData?.committeeAssignmentTitles ?? [];
  const assignmentTitleIdsKey = assignmentTitles.map((title) => title.id).join(",");

  const assignedSections = useMemo(
    () => filterAssignedCrmSections(crmSections, assignedLegacyIds),
    [assignedLegacyIds, crmSections]
  );
  const waitingOnAssignedSections = needsAssignedSections && sectionsLoading;
  const scopes = useMemo(() => {
    if (waitingOnAssignedSections) return [];
    return resolveGroupEmailScopes({ canAccessNationalGroupEmail, assignedSections });
  }, [assignedSections, canAccessNationalGroupEmail, waitingOnAssignedSections]);

  const defaultScopeKey = defaultGroupEmailScopeKey(scopes, preferredSectionId);
  const selectedScope =
    scopes.find((scope) => scope.key === selectedScopeKey) ??
    scopes.find((scope) => scope.key === defaultScopeKey) ??
    scopes[0] ??
    null;
  const isNationalScope = selectedScope?.national === true;

  const {
    data: nationalSectionData,
    loading: nationalSectionLoading,
    error: nationalSectionError
  } = useQuery(NATIONAL_SECTION_QUERY, {
    variables: { input: { search: NATIONAL_SECTION_NAME } },
    skip: !active || !isNationalScope
  });
  const nationalSectionId = useMemo(
    () => (isNationalScope ? resolveNationalSectionId(nationalSectionData?.crmSections?.nodes) : undefined),
    [isNationalScope, nationalSectionData?.crmSections?.nodes]
  );
  const nationalSectionReady = !isNationalScope || !nationalSectionLoading;
  const nationalSectionMissing = Boolean(
    isNationalScope && nationalSectionReady && !nationalSectionId && !nationalSectionError
  );
  const sectionIdForQuery = isNationalScope ? nationalSectionId : selectedScope?.sectionId;
  const scopeReady = Boolean(sectionIdForQuery);
  const committeesBusy = committeesLoading || (isNationalScope && nationalSectionLoading);
  const committeesFailed = committeesError;

  const selectedCommitteeIdsKey = useMemo(
    () => Array.from(selectedCommitteeIds).sort().join(","),
    [selectedCommitteeIds]
  );
  const selectedCommittees = committees.filter((committee) => selectedCommitteeIds.has(committee.id));
  const recipientResult = useMemo(() => {
    if (titlesLoading || titlesError || (assignmentTitleIdsKey && selectedTitleIds.size === 0)) {
      return EMPTY_RESULT;
    }
    const derived = collectEmailsFromAssignmentNodes(
      assignmentNodes,
      assignmentRoleFilter({
        catalogTitleIds: assignmentTitleIdsKey ? assignmentTitleIdsKey.split(",") : [],
        selectedTitleIds
      })
    );
    return {
      emails: derived.emails,
      participantCount: derived.participantCount,
      excludedCount: derived.excludedCount
    };
  }, [assignmentNodes, assignmentTitleIdsKey, selectedTitleIds, titlesError, titlesLoading]);

  useEffect(() => {
    if (!active) {
      didSeedTitlesRef.current = false;
      setField("bcc");
      setCoreOnly(false);
      setSelectedCommitteeIds(new Set());
      setSelectedTitleIds(new Set());
      setAssignmentNodes([]);
      setRecipientsTruncated(false);
      setSectionsTruncated(false);
      setSelectedScopeKey("");
      assignmentCacheRef.current = new Map();
      return;
    }

    if (!needsAssignedSections) {
      setCrmSections([]);
      setSectionsLoading(false);
      setSectionsError(false);
      setSectionsTruncated(false);
      return;
    }

    let cancelled = false;
    setSectionsLoading(true);
    setSectionsError(false);
    setSectionsTruncated(false);

    void fetchCrmSectionsForGroupEmail(client)
      .then(({ sections, truncated }) => {
        if (cancelled) return;
        setCrmSections(sections);
        setSectionsTruncated(truncated);
      })
      .catch(() => {
        if (cancelled) return;
        setCrmSections([]);
        setSectionsError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setSectionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, needsAssignedSections, active]);

  useEffect(() => {
    if (!active || scopes.length === 0) return;
    setSelectedScopeKey((previous) => {
      if (previous && scopes.some((scope) => scope.key === previous)) return previous;
      return defaultGroupEmailScopeKey(scopes, preferredSectionId) ?? "";
    });
  }, [active, preferredSectionId, scopes]);

  useEffect(() => {
    if (!active || !assignmentTitleIdsKey || didSeedTitlesRef.current) return;
    didSeedTitlesRef.current = true;
    setSelectedTitleIds(new Set(assignmentTitleIdsKey.split(",")));
  }, [assignmentTitleIdsKey, active]);

  useEffect(() => {
    if (!active) return;
    assignmentCacheRef.current = new Map();
    setSelectedCommitteeIds(new Set());
    setAssignmentNodes([]);
    setRecipientsTruncated(false);
  }, [coreOnly, active, sectionIdForQuery]);

  useEffect(() => {
    if (!active || !scopeReady || !sectionIdForQuery) {
      setCommittees([]);
      setCommitteesLoading(false);
      setCommitteesError(false);
      setCommitteesTruncated(false);
      return;
    }

    let cancelled = false;
    setCommittees([]);
    setCommitteesLoading(true);
    setCommitteesError(false);
    setCommitteesTruncated(false);

    void fetchActiveCommitteesForGroupEmail(client, { sectionId: sectionIdForQuery, core: coreOnly })
      .then(({ committees: nextCommittees, truncated }) => {
        if (cancelled) return;
        const visibleIds = new Set(nextCommittees.map((committee) => committee.id));
        setCommittees(nextCommittees);
        setCommitteesTruncated(truncated);
        setSelectedCommitteeIds((previous) => new Set([...previous].filter((id) => visibleIds.has(id))));
      })
      .catch(() => {
        if (cancelled) return;
        setCommittees([]);
        setCommitteesError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setCommitteesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, coreOnly, active, scopeReady, sectionIdForQuery]);

  useEffect(() => {
    if (!active || !selectedCommitteeIdsKey || !canViewPersonContact) {
      setAssignmentNodes([]);
      setRecipientsLoading(false);
      setRecipientsTruncated(false);
      return;
    }

    const selectedIds = selectedCommitteeIdsKey.split(",");
    for (const cachedId of Array.from(assignmentCacheRef.current.keys())) {
      if (!selectedIds.includes(cachedId)) {
        assignmentCacheRef.current.delete(cachedId);
      }
    }

    const applyCache = () => {
      const nodes = selectedIds.flatMap((id) => assignmentCacheRef.current.get(id)?.nodes ?? []);
      const truncated = selectedIds.some((id) => assignmentCacheRef.current.get(id)?.truncated === true);
      setAssignmentNodes(nodes);
      setRecipientsTruncated(truncated);
    };

    const missingIds = selectedIds.filter((id) => !assignmentCacheRef.current.has(id));
    if (missingIds.length === 0) {
      applyCache();
      setRecipientsLoading(false);
      return;
    }

    let cancelled = false;
    setRecipientsLoading(true);

    void fetchGroupEmailParticipantEmails(client, { committeeIds: missingIds })
      .then((nextResult) => {
        if (cancelled) return;
        for (const [committeeId, result] of Object.entries(nextResult.byCommittee)) {
          assignmentCacheRef.current.set(committeeId, { nodes: result.nodes, truncated: result.truncated });
        }
        applyCache();
        const incomplete = nextResult.truncated || nextResult.failedCommitteeIds.length > 0;
        setRecipientsTruncated(
          incomplete || selectedIds.some((id) => assignmentCacheRef.current.get(id)?.truncated === true)
        );
        if (incomplete) {
          showErrorSnackbar("Not all email addresses loaded", {
            subtitle: "You can still copy or open email with the addresses that loaded."
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        applyCache();
        if (assignmentCacheRef.current.size === 0) {
          setRecipientsTruncated(false);
          showErrorSnackbar("Email addresses not loaded", { subtitle: "Please try again later." });
          return;
        }
        setRecipientsTruncated(true);
        showErrorSnackbar("Not all email addresses loaded", {
          subtitle: "You can still copy or open email with the addresses that loaded."
        });
      })
      .finally(() => {
        if (cancelled) return;
        setRecipientsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canViewPersonContact, client, active, selectedCommitteeIdsKey]);

  const selectedAvailableCount = committees.filter((committee) => selectedCommitteeIds.has(committee.id)).length;
  const areAllVisibleSelected = committees.length > 0 && selectedAvailableCount === committees.length;
  const atCommitteeCap = selectedCommitteeIds.size >= GROUP_EMAIL_MAX_SELECTED_COMMITTEES;
  const areAllTitlesSelected =
    assignmentTitles.length > 0 && assignmentTitles.every((title) => selectedTitleIds.has(title.id));
  const hasRolesSelected = assignmentTitles.length === 0 || selectedTitleIds.size > 0;
  const hasEmails = recipientResult.emails.length > 0;
  const actionsDisabled =
    copying ||
    recipientsLoading ||
    committeesLoading ||
    titlesLoading ||
    Boolean(titlesError) ||
    !hasEmails ||
    selectedCommittees.length === 0 ||
    !hasRolesSelected;

  const toggleCommittee = (committeeId: string) => {
    if (committeesBusy) return;
    setSelectedCommitteeIds((previous) => {
      const next = new Set(previous);
      if (next.has(committeeId)) {
        next.delete(committeeId);
      } else if (next.size < GROUP_EMAIL_MAX_SELECTED_COMMITTEES) {
        next.add(committeeId);
      }
      return next;
    });
  };

  const handleSelectAllCommittees = () => {
    if (committeesBusy || committees.length === 0) return;
    setSelectedCommitteeIds((previous) => {
      if (areAllVisibleSelected) {
        const visibleIds = new Set(committees.map((committee) => committee.id));
        return new Set([...previous].filter((id) => !visibleIds.has(id)));
      }
      const next = new Set(previous);
      for (const committee of committees) {
        if (next.size >= GROUP_EMAIL_MAX_SELECTED_COMMITTEES) break;
        next.add(committee.id);
      }
      return next;
    });
  };

  const toggleTitle = (titleId: string) => {
    setSelectedTitleIds((previous) => {
      const next = new Set(previous);
      if (next.has(titleId)) {
        next.delete(titleId);
      } else {
        next.add(titleId);
      }
      return next;
    });
  };

  const handleSelectAllTitles = () => {
    if (assignmentTitles.length === 0) return;
    setSelectedTitleIds(areAllTitlesSelected ? new Set() : new Set(assignmentTitles.map((title) => title.id)));
  };

  const copyAddresses = useCallback(async () => {
    if (recipientResult.emails.length === 0) return false;
    try {
      await copyTextToClipboard(formatEmailAddressList(recipientResult.emails));
      return true;
    } catch {
      showErrorSnackbar("Email addresses not copied", { subtitle: "Please try again later." });
      return false;
    }
  }, [recipientResult.emails]);

  const handleCopyAddresses = useCallback(async () => {
    setCopying(true);
    try {
      const copied = await copyAddresses();
      if (copied) {
        showSnackbar("Email addresses copied");
      }
    } finally {
      setCopying(false);
    }
  }, [copyAddresses]);

  const handleOpenEmail = useCallback(async () => {
    if (recipientResult.emails.length === 0) return;
    const href = buildCommitteeMailtoHref({
      emails: recipientResult.emails,
      field,
      subject: groupEmailMailtoSubject(selectedCommittees.map((committee) => committee.name))
    });

    if (isMailtoTooLong(href)) {
      setCopying(true);
      try {
        const copied = await copyAddresses();
        if (copied) {
          showSnackbar("This list is too long to open in email. Addresses copied instead.");
        }
      } finally {
        setCopying(false);
      }
      return;
    }

    openMailtoHref(href);
  }, [copyAddresses, field, recipientResult.emails, selectedCommittees]);

  const committeeHeader =
    selectedAvailableCount === 0
      ? `Committees (${committees.length}${coreOnly ? ", core" : ""})`
      : `Committees (${selectedAvailableCount} of ${committees.length}${coreOnly ? ", core" : ""})`;

  const scopeSection = (
    <section className="flex flex-col gap-3">
      <SubSectionHeading>Scope</SubSectionHeading>
      {waitingOnAssignedSections && <GroupEmailLoadingStatus>Loading your section…</GroupEmailLoadingStatus>}
      {sectionsError && (
        <p className="text-body-medium text-sys-brand-error" role="alert">
          Unable to load sections. Try again later.
        </p>
      )}
      {!waitingOnAssignedSections &&
        !sectionsError &&
        needsAssignedSections &&
        assignedSections.length === 0 &&
        !canAccessNationalGroupEmail && (
          <p className="text-body-medium text-sys-brand-error" role="alert">
            Your assigned section was not found.
          </p>
        )}
      {sectionsTruncated && (
        <Banner
          variant="warn"
          inline
          description="The section list is incomplete. Some assigned sections may be missing."
          testId="group-email-sections-truncated"
        />
      )}
      {scopes.length === 1 && selectedScope && (
        <div>
          <p className="text-body-large">{selectedScope.label}</p>
          <p className="text-body-medium text-sys-brand-on-surface-variant">{selectedScope.description}</p>
        </div>
      )}
      {scopes.length > 1 && selectedScope && (
        <Select
          label="Committee Scope"
          data-testid="group-email-scope"
          value={selectedScope.key}
          onSelect={setSelectedScopeKey}
          options={scopes.map((scope) => ({ value: scope.key, label: scope.label }))}
        />
      )}
    </section>
  );

  const committeesSection = (
    <GroupEmailCheckboxList
      heading={committeeHeader}
      items={committees.map((committee) => ({
        id: committee.id,
        label: committee.name,
        disabled: !selectedCommitteeIds.has(committee.id) && atCommitteeCap
      }))}
      selectedIds={selectedCommitteeIds}
      onToggle={toggleCommittee}
      onSelectAll={handleSelectAllCommittees}
      selectAllLabel={areAllVisibleSelected ? "Deselect all" : "Select all"}
      selectAllTestId="group-email-select-all-committees"
      selectAllAriaLabel={areAllVisibleSelected ? "Deselect all committees" : "Select all committees"}
      listTestId="group-email-committees-list"
      namePrefix="group-email-committee"
      filterSlot={
        <Checkbox name="group-email-core" label="Core Committees Only" checked={coreOnly} onChange={setCoreOnly} />
      }
      footerSlot={
        atCommitteeCap ? (
          <p className="text-body-medium text-sys-brand-on-surface-variant" role="status">
            Select up to {GROUP_EMAIL_MAX_SELECTED_COMMITTEES} committees at a time. Split larger sends into batches.
          </p>
        ) : null
      }
    >
      {committeesBusy && <GroupEmailLoadingStatus>Loading committees…</GroupEmailLoadingStatus>}
      {nationalSectionError && (
        <p className="text-body-medium text-sys-brand-error" role="alert">
          Unable to load National Section. Try again later.
        </p>
      )}
      {nationalSectionMissing && (
        <p className="text-body-medium text-sys-brand-error" role="alert">
          National Section was not found. National committees cannot be loaded without it.
        </p>
      )}
      {committeesFailed && (
        <p className="text-body-medium text-sys-brand-error" role="alert">
          Unable to load committees. Try again later.
        </p>
      )}
      {committeesTruncated && (
        <Banner
          variant="warn"
          inline
          description="The committee list is incomplete. Narrow the scope or split the send."
          testId="group-email-committees-truncated"
        />
      )}
      {!committeesBusy &&
        !committeesFailed &&
        !sectionsError &&
        !nationalSectionError &&
        !nationalSectionMissing &&
        committees.length === 0 && (
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            {waitingOnAssignedSections ? "Loading your section…" : "No committees found for this scope."}
          </p>
        )}
    </GroupEmailCheckboxList>
  );

  const rolesList = (
    <GroupEmailCheckboxList
      heading="Assignment Roles"
      items={assignmentTitles.map((title) => ({ id: title.id, label: title.title }))}
      selectedIds={selectedTitleIds}
      onToggle={toggleTitle}
      onSelectAll={handleSelectAllTitles}
      selectAllLabel={areAllTitlesSelected ? "Deselect all" : "Select all"}
      selectAllTestId="group-email-select-all-roles"
      selectAllAriaLabel={areAllTitlesSelected ? "Deselect all assignment roles" : "Select all assignment roles"}
      listTestId="group-email-roles-list"
      namePrefix="group-email-role"
      variant={layout === "page" ? "boxed" : "plain"}
      footerSlot={<p className="text-body-medium text-sys-brand-on-surface-variant">Active members only.</p>}
    >
      {titlesLoading && <GroupEmailLoadingStatus>Loading roles…</GroupEmailLoadingStatus>}
      {titlesError && (
        <p className="text-body-medium text-sys-brand-error" role="alert">
          Unable to load assignment roles. Try again later.
        </p>
      )}
    </GroupEmailCheckboxList>
  );

  const recipientAndAddTo = (
    <>
      <GroupEmailRecipientStatus
        visible={selectedCommittees.length > 0 && hasRolesSelected && !titlesError}
        loading={recipientsLoading || titlesLoading}
        emailCount={recipientResult.emails.length}
        excludedCount={recipientResult.excludedCount}
        truncated={recipientsTruncated}
      />
      <GroupEmailAddToField field={field} onChange={setField} emailCount={recipientResult.emails.length} />
    </>
  );

  const actions = (
    <GroupEmailActions
      onCopy={() => void handleCopyAddresses()}
      onOpen={() => void handleOpenEmail()}
      onCancel={onClose}
      showCancel={layout === "sheet"}
      disabled={actionsDisabled}
      copying={copying}
    />
  );

  const formBody =
    layout === "page" ? (
      <div className="flex flex-col gap-6" data-testid="group-email-committees-form">
        {scopeSection}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:items-start">
          {committeesSection}
          {rolesList}
        </div>
        {recipientAndAddTo}
        {actions}
      </div>
    ) : (
      <div className="flex flex-col gap-6" data-testid="group-email-sheet">
        {scopeSection}
        {committeesSection}
        {rolesList}
        {recipientAndAddTo}
      </div>
    );

  if (layout === "sheet") {
    return (
      <SideSheetModal
        isSidebarOpen={active}
        setSidebarOpen={(isOpen) => {
          if (!isOpen) onClose?.();
        }}
        title="Group Email"
        disableFocusManagement
        actions={actions}
      >
        {formBody}
      </SideSheetModal>
    );
  }

  return formBody;
}
